/**
 * API 请求客户端
 *
 * 翻译自 user/utils/luch-request，但做了现代化：
 *   - 不依赖 luch-request，直接 fetch
 *   - 默认自动加 JWT（从 localStorage 读）
 *   - 统一响应格式：{ code, data, msg }
 *   - 后期阶段 5 会切换 BASE_URL 到后端 /api/dk
 *
 * 注意：当前阶段 3 全部走 mock，BASE_URL 仅用于将来的真实请求。
 */

/**
 * API 基础地址：
 *   1) 显式配置 NEXT_PUBLIC_API_URL 时优先使用；
 *   2) 否则跟随当前页面访问的 host（如手机用 100.x.x.x:3001 打开时，
 *      请求自动打到 100.x.x.x:8000，避免写死 localhost 导致真机无法访问）。
 */
function resolveBaseUrl(): string {
  if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }
  return "http://localhost:8000";
}
export const BASE_URL = resolveBaseUrl();

export type ApiResponse<T> = {
  code: number;
  data: T;
  msg?: string;
};

export class ApiError extends Error {
  code: number;
  data?: unknown;
  constructor(code: number, msg: string, data?: unknown) {
    super(msg);
    this.code = code;
    this.data = data;
  }
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("xiadanuser_jwt");
}

/**
 * Saleor JWT_TTL_ACCESS 默认仅 5 分钟 —— 过期后所有 /api/dk 请求 401。
 * 用登录时保存的 refreshToken（默认 30 天）换新 token 并重试一次。
 */
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const rt = localStorage.getItem("xiadanuser_refresh");
  if (!rt) return null;
  refreshing ??= (async () => {
    try {
      const r = await fetch(`${BASE_URL}/graphql/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `mutation TokenRefresh($refreshToken: String!) {
            tokenRefresh(refreshToken: $refreshToken) { token errors { message } }
          }`,
          variables: { refreshToken: rt },
        }),
      });
      const d = await r.json();
      const token = d?.data?.tokenRefresh?.token;
      if (token) {
        localStorage.setItem("xiadanuser_jwt", token);
        return token as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      // 下次过期再刷新（ refreshToken 本身长期有效，可重复换新 token）
      setTimeout(() => (refreshing = null), 1000);
    }
  })();
  return refreshing;
}

export async function http<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const doFetch = () => {
    const token = getToken();
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    };
    if (path.includes("/user/address")) {
      // eslint-disable-next-line no-console
      console.log("[DK_HTTP]", path, "token?", !!token, "tokenLen=", token?.length, "head=", Object.keys(headers));
    }
    return fetch(`${BASE_URL}${path}`, { ...options, headers });
  };

  let res = await doFetch();

  // 401 → 尝试用 refreshToken 换新 token 重试一次（refresh 失败则维持 401 语义）
  if (res.status === 401 && !(options as any)._retried) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doFetch();
    }
  }

  const json: ApiResponse<T> = await res.json().catch(() => ({
    code: -1,
    data: null as T,
    msg: `网络错误 (HTTP ${res.status})`,
  }));

  // 适配后端 ok/error 格式
  if (json.code === undefined) {
    if ((json as any).ok === true) {
      json.code = 1;
      json.data = (json as any).data;
    } else if ((json as any).ok === false) {
      json.code = 0;
      json.msg = (json as any).error || (json as any).msg || "请求失败";
      json.data = null as T;
    } else if ((json as any).error) {
      // 后端 401/403 等返回 { error: "Unauthorized", detail: "..." }
      json.code = res.status;
      json.msg = (json as any).detail || (json as any).error || "请求失败";
      json.data = null as T;
    } else {
      json.code = -1;
      json.msg = "未知响应格式";
      json.data = null as T;
    }
  }

  if (json.code !== 1) {
    throw new ApiError(json.code, json.msg || "请求失败", json.data);
  }

  return json;
}

/** 简化 GET 请求（参数放 URL search） */
export async function httpGet<T = unknown>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
): Promise<ApiResponse<T>> {
  let url = path;
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join("&");
    if (qs) url += (path.includes("?") ? "&" : "?") + qs;
  }
  return http<T>(url, { method: "GET" });
}

export async function httpPost<T = unknown>(
  path: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  return http<T>(path, {
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
}
